/**
 * Sign-in, step 1: the email. Posts `POST /auth/otp/request` in the person's
 * chosen language and hands the verification to the code screen.
 *
 * The address travels in component state only — never the URL (history and
 * logs keep URLs) and never storage. The server answers identically for a
 * known and an unknown address, so nothing here may say "no account".
 *
 * At the end of the intake answered without an account (`D262`) the same
 * screen asks for the same address under the intake's words: the answers are
 * kept, and the e-mail is what turns them into a plan. The request, the code
 * and the account are the same either way.
 */
import {
  "auth.email_field_label" as emailFieldLabel,
  "auth.email_field_placeholder" as emailFieldPlaceholder,
  "auth.email_heading" as emailHeading,
  "auth.email_invalid" as emailInvalid,
  "auth.email_lede" as emailLede,
  "auth.email_submit" as emailSubmit,
  "auth.email_submitting" as emailSubmitting,
  "auth.no_account" as noAccount,
  "auth.save_heading" as saveHeading,
  "auth.save_lede" as saveLede,
  "auth.save_review" as saveReview,
} from "@repo/i18n";
import { type FormEvent, useCallback, useState } from "react";
import { useApi } from "../../data/api";
import { Button, TextField } from "../../ui/controls";
import { AuthFrame } from "./auth-frame";
import { appI18n } from "./i18n";
import { normalizeEmail } from "./sign-in";

export interface CodeRequest {
  readonly email: string;
  readonly sentAt: number;
  readonly verificationId: string;
}

/** Why the address is asked: to sign in, or to keep the intake's answers. */
export type EmailIntent = "sign-in" | "save-plan";

export interface EmailScreenProps {
  /** Filled when the person came back through "Trocar e-mail". */
  initialEmail: string;
  /** `"sign-in"` when absent. */
  intent?: EmailIntent;
  /**
   * The quiet door under the button: "Ainda não tenho conta" on sign-in,
   * "Revisar respostas" at the end of the intake. Absent, no door is drawn.
   */
  onDoor?: () => void;
  onSent: (request: CodeRequest) => void;
}

const WORDS: Readonly<
  Record<
    EmailIntent,
    {
      readonly door: () => string;
      readonly heading: () => string;
      readonly lede: () => string;
    }
  >
> = {
  "save-plan": { door: saveReview, heading: saveHeading, lede: saveLede },
  "sign-in": { door: noAccount, heading: emailHeading, lede: emailLede },
};

export function EmailScreen(props: EmailScreenProps) {
  const api = useApi();
  const locale = appI18n.useLocale();
  const errorMessage = appI18n.useErrorMessage();
  const { onSent } = props;
  const [value, setValue] = useState(props.initialEmail);
  const [error, setError] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState(false);

  const change = useCallback((next: string) => {
    setValue(next);
    setError(undefined);
  }, []);

  const submit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (pending) {
        return;
      }
      const email = normalizeEmail(value);
      if (email === null) {
        setError(emailInvalid());
        return;
      }
      setPending(true);
      api
        .requestCode(email, locale)
        .then((output) => {
          onSent({
            email,
            sentAt: Date.now(),
            verificationId: output.verificationId,
          });
        })
        .catch((caught: unknown) => {
          setError(errorMessage(caught));
          setPending(false);
        });
    },
    [api, errorMessage, locale, onSent, pending, value]
  );

  const words = WORDS[props.intent ?? "sign-in"];
  return (
    <AuthFrame
      actions={
        <>
          <Button disabled={pending} size="lg" type="submit" variant="primary">
            {pending ? emailSubmitting() : emailSubmit()}
          </Button>
          {props.onDoor === undefined ? null : (
            <Button disabled={pending} onClick={props.onDoor} variant="ghost">
              {words.door()}
            </Button>
          )}
        </>
      }
      heading={words.heading()}
      lede={words.lede()}
      onSubmit={submit}
    >
      <TextField
        autoCapitalize="none"
        autoComplete="email"
        enterKeyHint="send"
        error={error}
        inputMode="email"
        label={emailFieldLabel()}
        name="email"
        onValueChange={change}
        placeholder={emailFieldPlaceholder()}
        type="email"
        value={value}
      />
    </AuthFrame>
  );
}
